<template>
    <div>  
        <!-- Supplios Formio Form Renderer - must have script active on page for now -->
        <div class="mb-3 d-flex">
            <!-- <div class="text-dark font-size-80 font-weight-500 text-uppercase mb-2">
                Sections
            </div> -->
            <ul
                v-if="hasPages"
                class="spl-formio-page-nav w-auto d-flex flex-wrap gap-2 nav bg-white rounded-lg p-2"
                role="tablist"
            >
                <li
                    v-for="page in allPages"
                    :key="page.path"
                    class="nav-item"
                >
                    <a
                        :class="{active: page.index === currentPageIndex}"
                        class="py-2 px-2 nav-link"
                        role="button"
                        @click="handlePageClick(page)"
                    >
                        {{ page.title }}
                        <b-icon 
                            v-if="showValidationHints"
                            :icon="page.isValid ? 'check' : 'x'" 
                            :variant="page.isValid ? 'success' : 'danger'" 
                            class=""
                        ></b-icon>
                    </a>
                </li>
            </ul>
        </div>

        <div 
            v-if="hasPages"
            class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom"
        >
            <h4 class="mb-0">{{ currentPageTitle }}</h4>
            <div>
                <div v-if="showValidationHints">
                    <span 
                        class="font-weight-bold font-size-80 text-uppercase mr-2"
                        :class="currentPageValidity ? 'text-success' : 'text-danger'"
                    >
                        Page {{currentPageValidity ? 'Complete' : 'Incomplete'}}
                    </span>
                    <b-icon 
                        :icon="currentPageValidity ? 'check-circle-fill' : 'x-circle-fill'"
                        :variant="currentPageValidity ? 'success' : 'danger'"
                    ></b-icon>
                </div>
            </div>
        </div>

        <div>  <!-- Keep the element formio mounts to inside this other div - it freaks out if other sibling elments are conditional and changing -->
            <div 
                ref="formio-form" 
                class="spl-formio-form spl-formio-tight"
                :class="[
                    htmlMode ? 'spl-formio-form-htmlmode' : '',
                    readOnly ? 'spl-formio-form-readonly' : '',
                ]"
            ></div>
        </div>

        <div v-if="showFishbone" class="my-4">  <!-- PUT 'EXTRAS' FOR THE FORM HERE - custom things that you add based on whats in the form -->
            <div class="d-flex justify-content-center">  <!-- Fishbone Diagram wrapper -->
                <fishbone-diagram
                    ref="fishboneDiagram"
                    v-if="showFishbone"
                    v-bind="fishboneData"
                    @created="handleFishboneUpdated"
                    @updated="handleFishboneUpdated"
                ></fishbone-diagram>
            </div>
        </div>

        <div v-if="hasPreviousPage || hasNextPage" class="my-4 d-flex justify-content-between align-items-end">  <!-- Custom Page controls -->
            <div>
                <b-button
                    v-if="hasPreviousPage"
                    class="min-width-100 text-left"
                    variant="sharp-primary"
                    @click="handlePreviousPage"
                >
                    &larr; Previous<br>
                    <span class="font-size-70 font-weight-300">{{ previousPageObject.title }}</span>
                </b-button>
            </div>
            <div>
                <b-button
                    v-if="hasNextPage"
                    class="min-width-100 text-right"
                    variant="sharp-primary"
                    @click="handleNextPage"
                >
                    Next &rarr;<br>
                    <span class="font-size-70 font-weight-300">{{ nextPageObject.title }}</span>
                </b-button>
            </div>
        </div>

        <!-- <b-link @click="getCompleteSubmissionData">Get Submission</b-link> -->

    </div>
</template>

<script>
// This ONLY renders the form and emits data (and uploads files) ???? 
// does not make API calls - do that in parent - so likely should NOT have submit buttons in the Formio schema
// put them in parent and have that call methods or get data from here
// actually, might make more sense to have completely standalone component that GETs form submission data, saves drafts, saves submission, and reloads as needed
// maybe that's the one that wraps this?

/* Checking individual field validation:
this works:
formioUtils.eachComponent($vm0.form.instance.components, (comp) => {console.log(comp.checkValidity(), comp.key)})
calling isValid() instead of checkValidity() does NOT work 

NOTE - there seems to be a bug using FormioUtils.eachComponent() when looping over components inside COLUMNS
 so instead, just call .checkValidity() on each page... I think that will work for high-level validation checking

run this on each page in the form:
$vm0.form.instance.components.forEach(topcomp => console.log(topcomp.checkValidity()))


*/


import cloneDeep from 'lodash/cloneDeep';
import throttle from 'lodash/throttle';
import filter from 'lodash/filter';
import get from 'lodash/get';
import set from 'lodash/set';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
// MOVED TO GLOBAL ASYNC IMPORT: import FishboneDiagram from './FishboneDiagram.vue';

export default {
    name: 'FormioForm',
    components: {
        // FishboneDiagram,
    },
    mixins: [],
    props: {
        schema: {
            type: Object,
            required: true,
        },
        submissionData: {  // the EXISTING/INITIAL submission.data, if any - just data or whole submission?  just be consistent with whatever this passes back up
            // might want to add watcher to this in case parent updates it to set new submission data
            // but make sure parent is not looping back from emitted events from here... that would be too much... maybe infinite loop
            type: Object,
            required: false,
        },
        fileUrls: {
            // object that provides updated 'url' for each already-uploaded S3 file
            // keys are our backend s3file.id, values are the download_url with long querystring for authorized downloads
            type: Object,
            default: () => {},
        },
        formOptions: {  // make bring some out as top-level props, then merge them in a watcher
            // this is NOT reactive to changes on its own... just the Booleans below 
            // options here:  https://help.form.io/developers/form-renderer#form-renderer-options
            type: Object,
            default: () => {
                return {
                    renderMode: 'form',
                    //alwaysDirty: true,  // disabled my MH May 10 2022 - because you always have it false anyways
                }
            },
        },
        htmlMode: {  // if true, sets formOptions.renderMode = 'html'
            // have a watcher on this
            // all options for renderMode are 'form', 'html', 'flat', 'builder' -- we most likely only want form and html for displaying forms, but flat might be useful -- its like form, but flattens out wizards and such
            // you can make custom render modes too!
            // make sure to set additional class on form div when this is active so you can hide certain things
            type: Boolean,
            default: false,
        },
        readOnly: {
            // have a watcher on this
            type: Boolean,
            default: false,
        },
        parentId: {  // you SHOULD provide this -- the ID of the DemoWorkflowTask (will be .parent FK of the file)
            type: String,
            default: '',
        },
        parent: { // full parent object (ie: WorkflowTask) - to get stuff from 'context'
            type: Object,
            required: false,
        },
        loading: {  // set this to true to hide form when saving in parent???
            type: Boolean,
            default: false,
        },
        renderAllExtras: {  // should we force all 'extras' to be rendered, so we can potentially capture them from parent?  like force show fishbone, etc.
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            finalFormOptions: {},  // we'll merge things from various props and set here
            formioUtils: null,  // we'll get from window.FormioUtils in create - must have script tag on the page!
            formio: null,  // from window.Formio
            form: null,  // the result of new Formio.Form() -- we need the instance from it
            fileComponentKeys: null,  // array of keys for any FILE components in the schema -- need this so we can replace them with up-to-date URLs
            // CURRENT DATA ABOUT THE COMPLETED FORM
            localSubmissionData: null,  // we'll keep local copy of submission.data
            localIsValid: null,  // boolean of data validity - ENTIRE FORM
            // WIZARD BUTTONS
            wizardButtons: {  // only apply to wizard buttons 
                showCancel: false,
                showNext: true,
                showPrevious: true,
                showSubmit: false,
                cancel: false,
                next: true,
                previous: true,
                submit: false,
            },
            // maybe make some 'override' objects that will override all fields of that type on this form with things?
            // helpful for files, to update url, dir, name template, fileTypes, multiople, imageSize, fileMaxSize, etc.
            pageValidity: {},  // will be an object, keys are keys of pages, values are Boolean isValid for each one 
            pageStore: {},  // our 'store' of page info -- object of objects keyed by page.path, each object has 'title', 'isValid', anything else?
            // all sorts of functions to call to control pages.... this.form.setPage(3), etc.
            // SPECIAL COMPONENT SETTINGS - likely change this later
            fishboneComponentKey: 'causeInvestigation',  // used to get things from submission data AND schema
            fishboneData: {},  // we'll update using updateFishboneData method -- computed was not reactive enough
            // STASHED ARRAY OF TOP LEVEL INPUT KEYS - used when figuring out what to submit
            topLevelInputKeys: [],  // array of keys only (strings) of top-level input components
        }
    },
    watch: {
        /* htmlMode: {
            //immediate: true,
            handler: 'setFormOptions',  // call method on this instance
        } */
    },
    computed: {
        currentPageIndex() {
            return this.form?.page ?? null;
        },
        hasNextPage() {
            const pageCount = this.allPages.length;
            return !!pageCount && (this.currentPageIndex < (pageCount - 1)); 
        },
        hasPreviousPage() {
            return this.hasPages && this.currentPageIndex > 0;
        },
        nextPageObject() {
            // return full object from this.pageStore - { index, isValid, path, title, components[] }
            // pageStore is object with keys of page path ('page1', 'page2', etc.)
            return this.allPages.find(pg => pg.index === (this.currentPageIndex + 1)) ?? {};  // return empty object if no next page
        },
        previousPageObject() {
            // return full object from this.pageStore - { index, isValid, path, title, components[] }
            return this.allPages.find(pg => pg.index === (this.currentPageIndex - 1)) ?? {};  // return empty object if no next page
        },
        currentPagePath() {
            return this.form?.component?.key ?? '';  // there are multiple ways to get this... i picked this just because
        },
        currentPageValidity() {
            return this.currentPagePath ? this.pageStore[this.currentPagePath].isValid : null;
        },
        currentPageTitle() {
            return this.currentPagePath ? this.pageStore[this.currentPagePath].title : '';
        },
        currentPageComponents() {  // Returns a key-value object where the keys are the keys for each component in components and each key points to the corresponding component.
            // useful for knowing what's on the page for any custom display stuff
            const currentPageComponentsNested = this.currentPagePath ? this.pageStore[this.currentPagePath].components : [];
            if (this.formioUtils) {
                return this.formioUtils.flattenComponents(currentPageComponentsNested);
            } else {
                return {};
            }
        },
        hasPages() {
            return this.allPages.length > 1;  // changed to greater than 1
        },
        allPages() {
            // return array of your own page objects from pageStore, which includes validity
            return this.pageStore ? Object.values(this.pageStore) : [];
        },
        showValidationHints() {
            // only show them (page validation icons, Page Complete, etc.) when page is editable
            const formNotActive = this.htmlMode || this.readOnly;
            return !formNotActive;  // if not not active, then active :) and show validation hints
        },
        rowSourceComponentKeys() {
            // does this form(schema) have any fields with 'auto data' - ie: things you calculate yourself, outside of Formio
            // look at 'properties' of all the components
            // in components, under 'properties' add 'autoData': 'rowSource' or some other string type of autoData -- then look for those types here
            // ADD MORE autoData types later!
            const rowSourceQuery = {'properties.autoData': 'rowSource'};
            const rowSourceComponents = this.formioUtils.searchComponents(this.schema.components, rowSourceQuery);
            return rowSourceComponents? rowSourceComponents.map(rsc => rsc.key) : [];
        },
        fishboneOnPage() {
            return Object.keys(this.currentPageComponents).includes(this.fishboneComponentKey);
        },
        shouldShowFishbone() {
            // do we WANT to show the fishbone?  used to combine multiple things that control INTENT to show it
            // this is about the INTENT, not about whether we actually CAN show it (ie: whether we actually have enough data to do it)
            return this.fishboneOnPage || this.renderAllExtras;
        },
        showFishbone() {
            // show the fishbone diagram component if we have branchesForFishbone, and component with fishbone data is on current page (in currentPageComponents)
            const hasFishboneData = !!this.fishboneData?.branches && this.fishboneData.branches.length > 0;
            return this.shouldShowFishbone && hasFishboneData;
        },
    },
    created() {
        if (window.Formio) {
            this.formio = window.Formio;
            //console.log('We HAVE Formio');
        } else {
            console.error('NO FORMIO ON WINDOW - from FormioForm component');
        }
        if (window.FormioUtils) {
            this.formioUtils = window.FormioUtils;
            //console.log('We HAVE FormioUtils');
        } else {
            console.error('NO FORMIO UTILS ON WINDOW - from FormioForm component');
        }
        // merge all the options (before mounted)
        this.setFormOptions();
        // FOR DEBUG
        //console.log('Dumping schema from backend:', this.schema);
        // MAKE THROTTLED VERSION OF VALIDITY UPDATER
        this.throttledValidityChecker = throttle(this.updateValidity, 500);
    },
    mounted() {
        this.setForm();
    },
    destroyed() {
        if (this.form) {  // don't destroy this.formio... that's from the window script tag - destroy what you constructed in new Form()
            this.form.destroy(true);
        }
    },
    methods: {
        async setForm() {
            const formElement = this.$refs['formio-form'];
            if (!formElement) {
                console.error('could not get the formio-form ref!');
                return;
            }
            if (!this.formio) {
                console.error('could not get Formio from window, so cannot render form');
            }
            const formSchema = cloneDeep(this.schema);  // or not clone it?  let it be reactive?
            // MAKE CHANGES TO THE SCHEMA BASED ON THE CONTEXT -- HAVE TO DO IT HERE, BEFORE MAKING THE FORM - the FormioUtils stuff works on schema.components 
            // loop through all pages and turn off wizard nav buttons?
            const schemaFileComponents = this.formioUtils.searchComponents(formSchema.components, {type: 'file'});  
            if (schemaFileComponents) {
                // set the array of fileComponentKeys so you can update the submission data URLs if needed
                this.fileComponentKeys = schemaFileComponents.map(fc => fc.key);  // Array of keys of file components so we can update URLs in submission data later
                // now set the 'dir' on each file component - sets the 'dir' of the file uploads -- 'dir' will be part of posted data together with the file - we use this to know the parent.id of the file being uploaded - maybe can add more info here too later
                this.formioUtils.eachComponent(schemaFileComponents, fileComp => {
                    //console.log(`Setting dir on fileComp to ${this.parentId} - dumping fileComp after making change:`);
                    fileComp.dir = this.parentId;
                    //console.log(fileComp);
                    //console.log('PATH for file component:', fileComp.path);
                });
            }
            // NOTE - to override things on all components in a form, you can set { components: {} } within formOptions, will merge those with schema before rendering - might be better...
            // IDEA - you could also pass in 'context' data as a hidden part of the submission?  or submission metadata?
            const finalFormOptions = cloneDeep(this.finalFormOptions);
            //console.log('finalFormOptions just before we create form', finalFormOptions);
            //this.form = this.formio.createForm(formElement, formSchema, finalFormOptions);  // Formio.createForm() already calls .ready -- it is just this:  return (new Form()).ready;
            this.form = await (new this.formio.Form(formElement, formSchema, finalFormOptions)).ready;  // I think this syntax allows for rendering in different modes, etc.
            //console.log('HERE IS this.form', this.form);
            // set this.pageValidity with keys, values of none for each
            const pageValidity = Object.fromEntries(this.form.allPages.map(pg => ([ pg.path, null ])));  // makes { page1: null, page2: null, ... }
            this.pageValidity = Object.assign({}, pageValidity);
            const pageStore = Object.fromEntries(this.form.allPages.map((pg, pgIndex) => {
                const currentIsValid = pg.checkData();  // OLD - NOT THOROUGH FOR NESTED COMPONENTS pg.checkValidity();  // we get initial value now, but will update it later in validity calls
                const pageObj = {  // our own page object that we'll use to display and validate, etc.
                    path: pg.path, // for referencing when its an array
                    index: pgIndex,  // can call this.form.setPage(pageIndex) to go there
                    title: pg.component.title,
                    isValid: currentIsValid,  // initial value... will get updated later
                    components: pg.components,  // could also get pg.originalComponent.components to get the JSON version
                    // lots more you can add to this...
                };
                return [pg.path, pageObj];
            }));
            this.pageStore = Object.assign({}, pageStore);
            // SET EXISTING SUBMISSION
            if (this.submissionData) {  // check that its a valid thing?  like an object with matching properties???
                /* JUST TESTING NESTED DATA vs. UNNESTED
                const notNested = {};
                const wfMeta = cloneDeep(this.submissionData.workflow.meta);
                Object.entries(wfMeta).forEach(([shortKey, val]) => {
                    const longKey = `workflow.meta.${shortKey}`;
                    notNested[longKey] = val;
                })
                console.log('notNested submission data', notNested);
                this.setSubmission(notNested); */
                this.setSubmission(this.submissionData);
            }
            // SETUP FORM EVENT LISTENERS
            this.form.on('change', (changedPayload) => {
                // despite what the formio docs say, it seems the 'changed' payload includes { changed, data, isValid }
                // changed is info about what changed, data is the full form data at this time, and isValid is the overall validity at this time
                this.handleChange(changedPayload);
            });
            this.form.on('error', (errorPayload) => {
                this.handleError(errorPayload);
            });
            this.form.on('formLoad', (e) => {  // maybe not emitted by form Wizard, only Webform (single page form)
                //console.log('formLoad event', e);
                this.onFormLoad(e);
            });
            this.form.on('render', (e) => {  // emitted by Webform (single form) or Wizard -- but only if there are fields on the current page!
                //console.log('form render event', e);
                this.onFormRender(e);
            });
            this.form.on('initialized', (e) => {  // only on Webform, not Wizard?  called after form is rendered, attached, and first change event
                //console.log('form is initialized - from event handler');
                this.onFormInitialized(e);
            });
            this.form.on('blur', (e) => {
                //console.log('blur', e);
                this.onComponentBlur(e);
            });
            this.form.on('editGridAddRow', () => {
                this.updateFishboneData();
            });
            this.form.on('editGridSaveRow', () => {
                this.updateFishboneData();
                this.updateAutoData();  // NEW FEB 25 2022 - needed to update 5Ys
            });
            this.form.on('editGridDeleteRow', () => {
                this.updateFishboneData();
            });
            this.form.on('nextPage', () => {
                this.onAnyPageChange();
            });
            this.form.on('prevPage', () => {
                this.onAnyPageChange();
            });
            this.form.on('wizardPageSelected', () => {
                // when page is changed in wizard and done rendering -- payload is page and index
                this.onAnyPageChange();
            });
            // TELL PARENT SETUP IS COMPLETE
            this.$emit('setup-complete');
            // ANYTHING ELSE YOU WANT TO DO ONCE NOW?
            // set the initial localSubmissionData -- other things need that, and it otherwise won't get set until the first change event
            // even though we just SET the form submission above, we'll grab it again here, from the form, in case the form mutated anything
            //const formSubmission = cloneDeep(this.form.submission);  // the full submission... data is nested inside
            //console.log('formSubmission', formSubmission);
            //this.setLocalSubmissionData(formSubmission.data);  // must do this before updateFishboneData
            this.updateFishboneData();
            // FINALLY, we'll check validity manually now that we have everything set -- from here on, we SHOULD be able to just get isValid status from 'change' event
            // if we set dirty = true, it will highlight the errors -- do this if we know we had existing data?
            //this.customUpdateValidity({ data: null, dirty: false, row: null, silent: true });
            console.log('from FormioForm - end of setForm - will call updateValidity()');
            this.updateValidity();
            this.topLevelInputKeys = this.getAllTopLevelInputComponentKeys();
        },
        setSubmission(submissionData) {
            // used to set initial submission on this.form, OR updates after parent saves new submission
            // we will likely be passing in only the submission.data -- so nest that, this.form.submission expects data as nested object
            // must already have this.form in ready state
            // ALSO, do any other prep-work on the submission -- such as replacing S3 file URLs with un-expired ones
            if (!this.form) {
                console.error('Cannot setSubmission -- this.form does not exist');
                // this DOES happen... has been happening since ~Aug/Sept 2022 in SmartFormFormio component, after user hits save
                // Not sure if it causes any REAL problems... 
                // if it does, then maybe we should move UP this.setLocalSubmissionData() so local submission data still gets set, 
                // but don't call any methods on the form - then when form is live, it uses localSubmissionData ???  figure out later if needed
                return;
            }
            const submission = { data: cloneDeep(submissionData) };
            // REPLACE THE FILE DOWNLOAD URLS IF ANY IN EXISTING SUBMISSION DATA
            if (this.fileComponentKeys) {
                this.fileComponentKeys.forEach(fcKey => {
                    // we use FormioUtils to get the value for each existing file upload, then change the file 'url' -- this MUTATES the submission
                    let fcFiles = this.formioUtils.getValue(submission, fcKey);
                    if (fcFiles) {  // if there were no uploads, fcFiles will be null -- if we have files, it will be Array of objects
                        fcFiles.forEach(fcFile => {
                            const fileId = fcFile.data.id;  // OUR fileId is nested under value.data.id -- that's where the 'custom' stuff for each file upload goes
                            const updatedUrl = this.fileUrls[fileId];
                            if (!updatedUrl) {
                                console.error(`Tried to get updated URL for file ID ${fileId} but was not in fileUrls`);
                            } else { // otherwise make the update -- it actually appears in 2 places in the submission data for the file field key -- in 'url' and in 'data.url' 
                                fcFile.url = updatedUrl;  // THIS IS THE ONE THAT MATTERS
                                fcFile.data.url = updatedUrl;  // in the nested custom data part -- not used by formio it seems, but might as well keep it updated
                            }
                        });
                    }
                    //console.log(fcFiles);
                });
            }
            // we will also set this.localSubmissionData now -- since this is only called when first rendering the form, so we have the data there.  otherwise the localSubmissionData is only updated on change events from the form
            this.setLocalSubmissionData(submission.data);
            //console.log('about to set form submission with this:', submission);
            this.form.setSubmission(submission);
            //this.form.submission = submission;
        },
        setLocalSubmissionData(newData) {
            // helper to set this.localSubmissionData on the Vue component, as passed from the form event -- since you'll do it once at the beginning, then on certain events from the form
            this.localSubmissionData = newData;   // should we use Object.assign here??? maybe...
        },
        handleChange(changedPayload) {
            // handle the 'change' event on the form - see notes above about what this includes
            const { data } = changedPayload;
            //console.log('FORM CHANGED!', data);
            // EXPERIMENTAL - seems changedPayload.data is not fresh
            //const freshFormData = this.form.getValue();
            //console.log('form.getValue()', freshFormData);
            // that didnt' work... try getValue() on each component
            //this.form.everyComponent(comp => console.log(comp.key, comp.getValue()));
            //const afterComponentGetValue = this.form.getValue();
            //console.log('form.getValue() after component getValue()', afterComponentGetValue);
            // didn't work... but when you actually change a value it gets updated -- if submission passed in was stale 
            //this.form.setPristine(false);
            //this.form.everyComponent(comp => console.log(comp.key, comp.getValue()));
            //const afterCompCheckData = this.form.getValue();
            //console.log('form.getValue() after component checkData()', afterCompCheckData);
            //console.log('FORM CHANGED!', changed, data, isValid);  // probably only need changed data, if any -- maybe its only the data that changed -- then you can get other things yourself
            //const submission = this.form.instance.submission;  // .submission or .submission.data?
            //this.localSubmission = Object.assign({}, submission);
            // just get from changedPayload.data instead... but that will ONLY be the data, no metadata about the submission... mayby OK for our needs
            // should we pass up this.form.submission instead????
            this.setLocalSubmissionData(data);
            //this.localIsValid = isValid;  // we will NOT set this here. it is always true when we first load the form for some reason...
            this.throttledValidityChecker();
            this.$emit('change', data);  // just pass up the data?  or complete payload?  just the data is easier
            // TODO - also pass up validation status??? isValid here acts funny....
        },
        handleError(errors) {
            // handle the 'error' event from the form -- errors is a list of errors than occurred
            console.log('FORM HAS ERROR!', errors);
            this.$emit('error', errors);  // just pass them up for now, unchanged
        },
        setFormOptions() {
            // do things to update it - called in created() and in watcher
            const htmlModeObj = this.htmlMode ? {renderMode: 'html'} : {};
            const readOnlyObj = this.readOnly ? {readOnly: true} : {readOnly: false};
            const wizardButtonsObj = this.wizardButtons ? {buttonSettings: {...this.wizardButtons}} : {};  // docs say options key should be 'buttonSettings' but source code shows 'buttons'
            //this.finalFormOptions = Object.assign({}, cloneDeep(wizardButtonsObj));
            this.finalFormOptions = Object.assign({}, {...this.formOptions}, htmlModeObj, readOnlyObj, wizardButtonsObj);
            // do something to re-render form?
        },
        customUpdateValidity({ data = null, dirty = false, row = null, silent = true } = {}) {
            // NOTE - we are using this in some parent components (SmartForm)
            // see formio docs for what each of these params mean -- it basically does a silent check on all current submission data in the form, does not highlight errors
            // use dirty = true or silent = false to highlight errors (not sure which does what)
            // use .checkData() to also run calculations, conditionals, AND validations
            //const isValid = this.form.instance.checkValidity(data, dirty, row, silent);
            // NOTE - if you pass data=null / row=null, it ONLY evaluates based on what's in current page it seems!
            const isValid = this.form.checkValidity(data, dirty, row, silent);
            // we will set the local value, emit to parent, and then return it
            //console.log(`customUpdateValidity: ${isValid}`);
            // NEW - June 22 2024 - highlight tabs with invalid fields
            this.checkAndHighlightTabsWithErrors({silent: (silent || !dirty)});
            // if (!silent || !!dirty) {
            //     // only do this if we are highlighting errors -- not on silent checks
            //     this.checkAndHighlightTabsWithErrors();
            // }
            this.localIsValid = isValid;
            this.$emit('is-valid', isValid);
            return isValid;
        },
        checkAndHighlightTabsWithErrors({silent = false} = {}) {
            // call this after checking validity to highlight any tabs with invalid fields -- that doesn't happen automatically
            // silent - if true, we will un-highlight tabs that went from invalid to valid, but not highlight tabs that went from valid to invalid
            // this only highlights/unhighlights the fields -- does not return anything
            // JUNE 22 2024 - need to highlight any TABS that have invalid fields -- not pages, but tabs, like used in SmartForms
            this.form.everyComponent((comp) => {
                if (comp.component.type === 'tabs') {
                    const invalidTabIndexes = [];
                    console.log('found a tabs component:', comp);
                    comp.components.forEach((tabChildComp) => {
                        // console.log(`tabChild on tab ${tabChildComp.tab}`, tabChildComp);
                        if (!tabChildComp.checkData()) {
                            invalidTabIndexes.push(tabChildComp.tab);
                        }
                    });
                    console.log('invalidTabIndexes', invalidTabIndexes);
                    // now find them in the tabs component and highlight them -- these are HTMLCollection objects, not arrays
                    const tabsCard = comp.element?.children[0];  // returns undefined if out of range, can also use .item(0) to return null if out of range
                    const tabsCardHeader = tabsCard?.children[0];
                    const tabsNav = tabsCardHeader?.children[0];  // the ul with the tab buttons
                    const tabButtons = tabsNav ? Array.from(tabsNav.children) : [];  // array of the tab button elements
                    tabButtons.forEach((tabButton, index) => {
                        if (invalidTabIndexes.includes(index)) {
                            if (!silent) {
                                tabButton.classList.add('spl-formio-invalid-tab-nav-item');
                            }
                        } else {
                            tabButton.classList.remove('spl-formio-invalid-tab-nav-item'); // 
                        }
                    });
                }
            });
        },
        updateValidity() {
            // see formio docs for what each of these params mean -- it basically does a silent check on all current submission data in the form, does not highlight errors
            // use dirty = true or silent = false to highlight errors (not sure which does what)
            // use .checkData() to also run calculations, conditionals, AND validations
            //const isValid = this.form.instance.checkValidity(data, dirty, row, silent);
            // NOTE - if you pass data=null / row=null, it ONLY evaluates based on what's in current page it seems!
            const isValid = this.form.checkData();  // OLD this.form.checkValidity();
            //const checkDataOk = this.form.checkData();  // this seems to be more thorough than checkValidity - it finds missing values in autoData editgrid components, for example
            // we will set the local value, emit to parent, and then return it
            //console.log(`UPDATING VALIDITY: ${isValid}`);
            // call this.checkAndHighlightTabsWithErrors() here too so the tabs get highlighted on all other validity updates
            this.checkAndHighlightTabsWithErrors({silent: true});
            this.localIsValid = isValid;
            this.$emit('is-valid', isValid);
            this.updatePageValidity();
            // this.getPartialSubmissionData();  // works, but don't need it on every single update.. will call from parent when needed
            return isValid;
        },
        updatePageValidity() {
            const pages = this.form.allPages;
            pages.forEach(pg => {
                this.pageStore[pg.path].isValid = pg.checkData();  // pg.checkValidity();
                //this.pageStore[pg.path].checkDataOk = pg.checkData();  // for testing - maybe should replace checkValidity
            });
        },
        getAllTopLevelInputComponentKeys() {
            // call this before getting the components for complete/partial submit
            const allCompsFlatMap = this.formioUtils.flattenComponents(this.schema.components);  // ALL components, top-level and nested, mapping of { key: component JSON }
            const allInputCompKeys = Object.keys(allCompsFlatMap);  // keys for all input components
            const isTopLevelInputKey = (componentKey) => {
                const splitPath = componentKey.split('.');  // array of strings, if you ever have hard-coded keys with array indexes this might cause problems, but I don't think you will?
                if (splitPath.length === 1) {
                    return allInputCompKeys.includes(splitPath[0]);  // if there is no dotted path, check if its in our input component keys
                } else if (splitPath.length > 1) {
                    splitPath.pop();  // .pop() removes the last item in array (mutates it!) and returns that popped item
                    // could also use splice - but it also returns the removed items as an array: removedItemsArray = originalArray.splice(-1);
                    const isNestedInput = splitPath.some(pKey => allInputCompKeys.includes(pKey));  // are any of the parents keys in our list of input components?
                    // if we set a dotted path just for shaping the data structure, then we won't have components for the parent path strings
                    // but if the key is a dotted path because its nested in another input component, then we will have a key for it
                    return !isNestedInput;  // if its not nested in another input, then we consider it top-level
                }
            }
            return allInputCompKeys.filter(icKey => isTopLevelInputKey(icKey));
        },
        checkValidationBeforeCompleteSubmit() {
            // CALLED FROM PARENT!!!
            // for now just call checkData - boolean
            // HIGHLIGHT ERRORS TOO? - can do that with passing dirty = true to this:
            // const isValid = this.form.checkValidity(data, dirty, row, silent);
            return this.form.checkData();
        },
        getComponentSubmitStatus(compInst) {
            // use this in either getCompleteSubmissionData or getPartialSubmissionData to standardize
            // pay attention to whether you should call this.form.setPristine(false) before checking things

            const key = compInst.key;  // key is whatever you input for 'API key' in form builder -- if you used dotted path, then its dotted path.  If you did not use dotted path (but its still nested in another component by formio) then its NOT dotted path here.
            //const path = compInst.path;  // the nested path of the data -- if its a row/field inside a higher-level component, then its different from key, modified by formio
            // key and path are same if you MANUALLY set the key to something nested using dotted path, and no further nesting by formio
            // we want to check keys
            const isTopLevelInput = this.topLevelInputKeys.includes(key);  //  this.isTopLevelInputKey(key);
            const hasSetValue = compInst ? compInst.hasSetValue : false;  // does the field have a value set?  we only want that when dealing with PARTIAL submissions so we don't override anything in saved data inadvertently
            // some components won't have .checkData() method, which also means they are not top-level components - so must be ready for that
            const dataValid = compInst ? compInst.checkData() : false; // is the field valid?  we only want to save those when dealing with PARTIAL submissions
            const isVisible = compInst ? compInst.visible : false;  // is the field visible?  or hidden because of conditionals, etc.
            const isReadOnly = compInst ? compInst.disabled : false;  // disabled inputs mean READ ONLY and should not be saved for either partial or complete submits
            const canBeSaved = isTopLevelInput && !isReadOnly;  // can only be saved if NOT read-only, and a TOP-LEVEL component  (because data for top-level components will include their children)
            // NOTE - July 24 2022 - isDirty is not working... probably has not been working all along - FIXED Sept 21 2022 - checking !isPristine instead
            //const isDirty = compInst ? compInst.isDirty : false;  // was it touched?  if a field isDirty, but no value, and empty is allowed, we'll submit it in partial -- because maybe user wanted to remove previous value
            const isDirty = compInst ? !compInst.isPristine : false;  // seems that checking isPristine = false is a much better way to know if its dirty -- both .dirty and .isDirty seem to not work as expected, but .isPristine seems OK
            //const hiddenButStillSave = !isVisible && !isDisabled;  // hidden, but we still want to save it.... we have hidden fields on purpose for pulling values out of objects in dropdowns, for example
            //const isValidAndVisible = dataValid && isVisible;
            // Combinations for PARTIAL submits
            // for PARTIAL submits, we allow setting 'blank' values that are NOT VALID (because they're blank, and as long as purposely set blank... hence 'isDirty')
            // because user is not ready to submit all yet, but they may want to remove a previous entry -- but only if NOT READ ONLY
            // before July 23 2022 we only included comps if they were VISIBLE, but that's no longer a requirement... just that they are not read-only - because we might have 'hidden' fields that we still need to set! (like in scorecard points)
            const emptyOnPurpose = canBeSaved && !hasSetValue && isDirty;  // && isVisible;  
            const hasValidValue = canBeSaved && hasSetValue && dataValid;  // && isVisible;
            // Sept 21 2022 - changed definition of includeInPartial -- no longer require isValid -- we'll save invalid/incomplete things... but user cannot submit COMPLETE form with these.... 
            //      this means that you may be updating workflow / related object things with invalid data... 
            //      maybe later you need to tell difference between hard/soft invalids???  or keep things in submission object, but don't update related objects until complete?  handle that if you find a real problem
            //      for now, you have suppliers complaining that their partial responses are getting lost when they have an ALMOST-complete complex field (like 5-why)
            // OLD const includeInPartial = hasValidValue || emptyOnPurpose;
            const includeInPartial = (canBeSaved && hasSetValue) || emptyOnPurpose;
            // Combinations for COMPLETE submits
            const includeInComplete = canBeSaved && dataValid;  // visible or hidden is OK, as long as its not disabled and valid value
            //const disabledAndVisible = isVisible && isDisabled;  // these are READ ONLY
            //const disabledAndNotVisible = !isVisible && isDisabled;  // should ALSO be READ ONLY - DO NOT REMOVE VALUE FOR THESE!  these are used for passing hidden data to the form for front-end calculations, etc
            // now we'll 'summarize' what we want for complete/partial submits (so we centralize the logic here)
            return {hasSetValue, dataValid, isVisible, isReadOnly, canBeSaved, isDirty, emptyOnPurpose, hasValidValue, includeInPartial, includeInComplete};
        },
        getCompleteSubmissionData() {  
            // CALLED FROM PARENT!!!
            // a custom way of getting the data for a 'COMPLETE' submission
            // why?  because if you hide a conditional field, the standard Formio behavior is to remove BOTH the key and value from submission data completely 
            // but this makes it hard to 'unset' any previously set values since backend will only look at which keys are coming in
            // 2 options - send blank values here, OR in backend, look at which keys we SHOULD be getting and set default ('' for now) for any missing keys for COMPLETE submissions
            // REALLY SHOULD DO THIS ON FRONTEND - if you have things that are dynamic (change to hidden or disabled based on other inputs) then front-end is good place to do this -- you COULD do it on backend too... but you have to write all that code first...
            // if field is DISABLED BUT DISPLAYED, that means its READ ONLY -- DO NOT SEND TO BACKEND
            // if field is HIDDEN, that means its UNSET - SEND TO BACKEND WITH ITS DEFAULT VALUE, or saved value if clearOnHide = false
            // JULY 23 2022 -- HIDDEN can also now include fields we've hidden on purpose, ie: to pull some value out of some object in a dropdown, for example - so need to change our logic
            
            // set pristine = false on the root form -- that forces hidden components (with clearOnHide) to reset to empty value
            // want to keep a hidden value?  then set clearOnHide=false -- it will keep it!
            this.form.setPristine(false);
            // then calling .getValue() on any COMPONENT instance (not on the form) will give the correct result for hidden fields
            // set it back to true when we're done?
            const compsToSubmit = [];
            const submissionData = {};  // we'll put things here if they are valid and set
            
            this.form.everyComponent((compInst) => {  // args are (componentInstance, siblingComponents(array), index)
                const key = compInst.key;  
                const submitStatus = this.getComponentSubmitStatus(compInst);  // object with bunch of booleans
                //console.log(`submitStatus for ${key}`, submitStatus);
                if (submitStatus.includeInComplete) {
                    compsToSubmit.push(compInst);
                    submissionData[key] = compInst.getValue();  // this SHOULD work even for hidden components (return empty value if hidden and clearOnHide=true)
                }
            });
            const keysToSubmit = compsToSubmit.map(c => c.key);
            const keysNotSubmit = this.topLevelInputKeys.filter(k => !keysToSubmit.includes(k));
            console.log('keys to submit', keysToSubmit);
            console.log('keys NOT to submit', keysNotSubmit);
            console.log('completeSubmissionData', submissionData);
            return submissionData;
        },
        getPartialSubmissionData() {
            // CALLING THIS DIRECTLY FROM PARENT, ONLY IF/WHEN NEEDED - because it is a bit expensive to do all the time
            // returns a subset of localSubmissionData, including only things that are valid
            // MAYBE WE --DO-- WANT TO SUBMIT INCOMPLETE FIELDS... (but still not invalid ones)
            // BECAUSE HOW ELSE WOULD WE DELETE A PREVIOUS VALUE FROM MERGED WORKFLOW DATA?  DO WE WANT TO DO THAT?
            // BUT WE MUST NOT SEND ANY FIELDS THAT ARE SOMEHOW HIDDEN (conditionals, etc.) - or else those could override things set in other places!
            // look at only top-level keys, check those - nested things may say they are valid, but if their parent is not valid, then its not useful
            // to get top-level keys, we need to look at incoming form JSON SCHEMA, not data... since formio will nest any keys with dotted path -- we care if they are top-level in our schema
            const partialSubmissionData = {};  // we'll put things here if they are valid and set
            const compsToSubmit = [];
            // don't iterate over keys and use form.getComponent(key) -- it doesn't seem to work for passing in a data path, even though docs suggest it should
            // instead, we'll iterate over all components using form.everyComponent(callback) - that seems to work, and iterates over all component INSTANCES (layout and inputs)
            this.form.everyComponent((compInst) => {  // args are (componentInstance, siblingComponents(array), index)
                const key = compInst.key;  // key is whatever you input for 'API key' in form builder -- if you used dotted path, then its dotted path.  If you did not use dotted path (but its still nested in another component by formio) then its NOT dotted path here.
                const submitStatus = this.getComponentSubmitStatus(compInst);  // object with bunch of booleans
                //console.log(`submitStatus for ${key}`, submitStatus);
                if (submitStatus.includeInPartial) { 
                    compsToSubmit.push(compInst);
                    partialSubmissionData[key] = compInst.getValue();
                }
            });
            const keysToSubmit = compsToSubmit.map(c => c.key);
            const keysNotSubmit = this.topLevelInputKeys.filter(k => !keysToSubmit.includes(k));
            console.log('keys to submit', keysToSubmit);
            console.log('keys NOT to submit', keysNotSubmit);
            console.log('partialSubmissionData', partialSubmissionData);
            return partialSubmissionData;
        },
        /* FROM FORM.IO SOURCE RELATING TO WHAT IS LOOKED AT WHEN SKIPPING VALIDATION -- USE THIS AS REFERENCE
        this is source from Component.js so the base component class
        shouldSkipValidation(data, dirty, row) {
            const rules = [
                // Force valid if component is read-only
                () => this.options.readOnly,
                // Do not check validations if component is not an input component.
                () => !this.hasInput,
                // Check to see if we are editing and if so, check component persistence.
                // isValueHidden() checks things on form, and also this: return (this.component.protected || !this.component.persistent || (this.component.persistent === 'client-only'));
                () => this.isValueHidden(),
                // Force valid if component is hidden.
                () => !this.visible,
                // Force valid if component is conditionally hidden.
                () => !this.checkCondition(row, data)
            ];
        */
        onComponentBlur() {
            // called anytime a component in the form is blurred -- good place to update things like diagrams, etc.
            this.updateFishboneData();
            this.$emit('blur');  // no payload for now
        },
        onFormLoad() {  // seems to only be called for Webforms (single page forms) not Wizards
            // when form has finished loading
            this.updateFishboneData();
            this.updateAutoData();
        },
        onFormRender(e) {
            // called when form is done rendering and completed the attach phase (attached to the DOM)
            // called in both single page forms (Webform) and multi-page (Wizard)
            this.$emit('render', e);
        },
        onFormInitialized() {  // seems to only be called for Webforms (single page forms) not Wizards
            // called when the form has completed the render, attach, and one initialization change event loop (ie: when its ready to go)
            //console.log('from onFormInitialized', e);
            //this.$emit('initialized');
        },
        onAnyPageChange() {
            // you have multiple ways of changing pages -- your own buttons, built-in buttons, page selector (built in and custom)
            // some of them emit different events (or no events by default)
            // this is common place to put things you want to do on any page change
            this.$nextTick(() => {
                this.updateFishboneData();
                this.updateAutoData();
            });
        },
        handlePageClick(pageObj) {
            // pageObj is your own page object from pageStore
            // this.form.setPage() is low-level... we have to emit event manually after it, chaining promises
            this.form.setPage(pageObj.index).then(() => this.form.emitWizardPageSelected());
        },
        async handleNextPage() {
            // just call .nextPage() method of the form instance, like from your own controls
            try {
                await this.form.nextPage();  // this returns a promise, rejected if there are errors
                // if its successful, it will check validation, change the page, and emit 'nextPage' event (unline this.form.setPage() -- which this uses inside)
            } catch {
                return;  // then there must have been errors on the page -- but we shouldn't need to do anything with them... they'll be shown
            }
        },
        async handlePreviousPage() {
            try {
                await this.form.prevPage();  // returns a promise
                // if its successful, it will check validation, change the page, and emit 'prevPage' event (unline this.form.setPage() -- which this uses inside)
            } catch {
                return;
            }
        },
        updateAutoData() {
            // update auto-calculated things in submission that YOU calculate outside of Formio
            // like rowSource, etc.
            if (this.rowSourceComponentKeys.length) {
                this.rowSourceComponentKeys.forEach(rscKey => this.updateRowSourceComponentData(rscKey));
            }
        },
        updateRowSourceComponentData(autoComponentKey) {
            // handles updating submission data for a SINGLE component with autoData: 'rowSource'
            // call this for each one you have in the form, each time it needs to be updated
            // look at current data, get data from rowSource, add any missing ones
            //console.log('FROM updateRowSourceComponentData');
            const autoComponentSchema = this.formioUtils.getComponent(this.schema.components, autoComponentKey);
            const autoComponentInstance = this.formioUtils.getComponent(this.form.components, autoComponentKey);  // the actual instance with .setValue() method on it
            const rowSourcePath = autoComponentSchema.properties.rowSourcePath;  // SHOULD BE STRING WITH DOTTED PATH TO WHERE ROWS COME FROM IN SUBMISSION DATA - can be nested in other components!
            const sourceRows = get(this.localSubmissionData, rowSourcePath);  // will be the array from this component
            const rowSourceKey = autoComponentSchema.properties.rowSourceKey;
            const rowSourceFilterRaw = autoComponentSchema.properties.rowSourceFilter;  // SHOULD BE IN FORMAT TO PASS TO LODASH filter() -- either 'booleanProperty' (string) or ['key', 'value'] (array) or {'prop1': val1, 'prop2': val2} (object to match)
            let rowSourceFilter;
            try {
                rowSourceFilter = JSON.parse(rowSourceFilterRaw);
            } catch {
                rowSourceFilter = '';
                console.error(`Tried to parse rowSourceFilter for auto component ${autoComponentKey} but JSON parse failed`, rowSourceFilterRaw);
            }
            // we'll likely need to transform rowSourceFilter into a real array or object if it was stored as one - from JSON
            //console.log('rowSourceKey', rowSourceKey);
            //console.log('rowSourceFilterRaw', rowSourceFilterRaw);
            //console.log('typeof rowSourceFilterRaw', typeof rowSourceFilterRaw);
            //console.log('rowSourceFilter', rowSourceFilter);
            //console.log('typeof rowSourceFilter', typeof rowSourceFilter);
            const sourceRowsFiltered = filter(sourceRows, rowSourceFilter);  // returns a NEW array, so does not mutate submission data
            //console.log('sourceRowsFiltered', sourceRowsFiltered);
            const requiredRowKeys = sourceRowsFiltered.map(sr => get(sr, rowSourceKey));  // so you can use dotted path in rowSourceKey
            const existingAutoRows = this.localSubmissionData[autoComponentKey];
            //console.log('existingAutoRows', existingAutoRows);
            const existingRowKeys = existingAutoRows.map(er => get(er, rowSourceKey));  // must be at same path in object as source row
            const missingRowKeys = difference(requiredRowKeys, existingRowKeys);  // should ADD these
            const extraRowKeys = difference(existingRowKeys, requiredRowKeys);  // should REMOVE these
            const correctRowKeys = intersection(requiredRowKeys, existingRowKeys);  // returns new array
            if (!missingRowKeys.length && !extraRowKeys.length) {
                //console.log(`NO MISSING OR EXTRA ROWS in ${autoComponentKey}`);
                return;  // bail early if there are no changes to make
            } else {
                //console.log(`HAVE ${missingRowKeys.length} MISSING and ${extraRowKeys} EXTRA ROWS in ${autoComponentKey}`);
            }
            const newRows = [];  // we'll make a new array for setValue
            // add ones from existing that should still be there
            existingAutoRows.forEach(ex => {
                const rowKey = get(ex, rowSourceKey);
                if (correctRowKeys.includes(rowKey)) {
                    newRows.push(cloneDeep(ex));
                }
            });
            // now add any missing ones
            missingRowKeys.forEach(missingKey => {
                const newRowObj = {};  // DO WE NEED TO SET ANY DEFAULT VALUES FOR OTHER THINGS???  MAYBE INSPECT OTHER COMPONENTS IN THE DATA COMPONENT WE ARE ADDING TO????
                set(newRowObj, rowSourceKey, missingKey);
                newRows.push(newRowObj);
            });
            // NEW VALUE IS NOW 'newRows' -- set it!
            autoComponentInstance.setValue(newRows);
            // then figure out what rows are missing, then set the value of the component
            // set/update value using setValue(value, flags) or setValueAt(index, value, flags) or updateValue(value, flags)
            // setValue updates the data and the view, updateValue just updates the data, not the view
            // see docs about custom components -- that's where this is best explained  https://help.form.io/developers/custom-components

        },
        updateFishboneData() {
            //console.log('FROM updateFishboneData');
            if (!this.shouldShowFishbone) {
                console.log('from updateFishboneData - shouldShowFishbone is false');
                return;  // bail early if not on page
            }
            // pull out data from submission data and schema, format for feeding to FishboneDiagram component, if there is any
            // return an OBJECT -- we v-bind the object as prop to component using v-bind="fishboneData"
            const componentProps = {  // DEFAULTS - make sure all the keys match component props!
                branches: [],
                title: 'Fishbone Diagram',
                subtitle: '',  // change to the report number or something?
                headText: '',  // the problem description
            };
            if (!this.fishboneComponentKey) {
                console.log('from updateFishboneData - fishboneComponentKey is false');
                return;
            }
            // set the easy stuff first like title/subtitle
            if (this.parent) {
                const taskNumber = this.parent?.task_number;
                componentProps.subtitle = taskNumber ? `For CAR # ${taskNumber}` : '';
            }
            componentProps.headText = this.localSubmissionData?.problemDescription || 'The problem description goes here';
            // NOW GET DATA FROM SUBMISSION DATA and BRANCH CATEGORIES FROM SCHEMA CUSTOM DATA
            const fishboneItemArray = this.localSubmissionData?.[this.fishboneComponentKey];  // NEW VERSION - everything is in one array, with category = 'mana' etc.
            const fishboneBranchArray = this.schema?.customData?.fishboneCategories;  // array of objects already in correct format
            if (!fishboneItemArray || !fishboneBranchArray) {
                console.log('from updateFishboneData - no fishboneItemArray or no fishboneBranchArray');
                return;  // if no data or fishbone categories, then bail
            }
            console.log('Starting to build fishbone branches');
            console.log('localSubmissionData', this.localSubmissionData);
            console.log('fishboneComponentKey', this.fishboneComponentKey);
            console.log('fishboneItemArray', fishboneItemArray);
            console.log('fishboneBranchArray', fishboneBranchArray);
            const makeBranchObject = (fishboneCat) => {
                // take your array of objects from schema.customData.fishboneCategories and put into correct format, add 'items' key with empty array to push items into
                return {
                    id: (fishboneCat.key ?? fishboneCat.id),  // I think you used 'key'
                    text: (fishboneCat.label ?? fishboneCat.text),  // I think you used 'label'
                    row: fishboneCat.row, // 'top' or 'bottom' - should already be correct
                    col: fishboneCat.col,  // integer index of column - 0,1,2 - should already be correct
                    items: [],  // ready to push items into it
                };
            }
            // now make the branches, and put them in a mapping for easy handling as you go through cause array 
            const fishboneBranchMapping = Object.fromEntries(fishboneBranchArray.map(cat => ([cat.key, makeBranchObject(cat)])));
            // finally, go through all causes and push into branch items
            const addCauseItem = (causeItem) => { // item format should be { id, text, highlight(boolean) }
                const itemObj = {
                    id: causeItem.causeGuid,
                    text: causeItem.potentialCause,
                    highlight: (causeItem.actualCause === 'yes'),
                };
                const branchObj = fishboneBranchMapping[causeItem.category];  // causeItem.category is 'mana', 'method', etc...
                if (branchObj) {
                    branchObj.items.push(itemObj);  // make sure you have existing branch for the category, or else something is screwed up
                }
            };
            fishboneItemArray.forEach(item => addCauseItem(item));
            // finally... set the fully-built branches on componentProps.branches - pull them out of the mapping object you made
            componentProps.branches = Object.values(fishboneBranchMapping);  // will be array of branch objects
            // finally... update this.fishboneData with componentProps
            this.fishboneData = Object.assign({}, componentProps);
        },
        handleFishboneUpdated(echartInstance) {
            // gets called whenever the fishbone component has echart that is created or updated
            // payload is the echartInstance itself
            // then you can use .refs to call .getDataURL() to get it
            const fishboneRef = this.$refs.fishboneDiagram;
            if (fishboneRef && echartInstance) {
                const fishboneDataURL = fishboneRef.getDataURL();
                this.$emit('fishbone-image', fishboneDataURL);  // emit the image dataURL as payload 
            }
        }
    }
}
</script>

<style lang="scss" scoped>

// NOTE - the scoping here probably prevents the CSS here from affecting the formio components themselves
//      - so you made a spl-formio-forms.scss file that holds stuff that should affect the formio components themselves

.spl-formio-page-nav {
    li.nav-item {
        a.nav-link {
            font-weight: 500;
            font-size: 0.8rem;
            border-radius: 0.5rem;
            border: 1px solid transparent;
            color: $gray-500;
            background-color: rgba(0,0,0,0.05);
            transition: 0.2s all;

            &:hover {
                color: $dark;
                background-color: rgba(0,0,0,0.08);
                border-color: $gray-300;
            }
            &.active {
                color: $dark;
                background-color: $primary-100;
                border-color: $primary-200;
            }
        }
    }
}

//nav[id|=wizard] {  // hide the normal wizard nav
//nav[id^="wizard"] {

.spl-formio-form .spl-formio-form-readonly {
    input, select, div.selection {
        background-color: var(--gray-100);
    }
}

.spl-formio-form::v-deep nav {  // >>> or ::v-deep is the 'deep' selector, meaning scoped styles will apply to child components too
    display: none;  // is this messing with tabs IN the form?  (not pages, but tabs components)?  
    // seems that it should, but we are saved because the scoping is not applied to the formio components?
}

</style>